import { getAppConfig } from '../config';
import { buildAbsoluteUrl } from './instruction-sheet-converter';
import type { InstructionSheetConverterResponse } from './instruction-sheet-converter';
import type {
  PresignedUploadRequest,
  FileUploadInfo,
  PresignedUploadResponse,
  FileUploadUrl,
  UploadCompletionRequest,
  UploadedFileInfo,
} from '@/lib/types';

// 後方互換性のため、型を再エクスポート
export type {
  PresignedUploadRequest,
  FileUploadInfo,
  PresignedUploadResponse,
  FileUploadUrl,
  UploadCompletionRequest,
  UploadedFileInfo,
} from '@/lib/types';

/**
 * 署名付きアップロードURL取得
 *
 * ## 機能概要
 * バックエンドから各ファイルのS3署名付きアップロードURLを取得します。
 *
 * ## 引数
 * - files: アップロード対象ファイルのリスト
 * - accountId: アカウントID
 * - authHeaders: 認証ヘッダー
 *
 * ## 戻り値
 * 署名付きURLとジョブIDを含むレスポンス
 */
export async function requestPresignedUrls(
  files: File[],
  accountId: string,
  authHeaders?: Record<string, string>,
): Promise<PresignedUploadResponse> {
  const config = getAppConfig();

  const request: PresignedUploadRequest = {
    files: files.map((file) => ({
      filename: file.name,
      size: file.size,
      content_type: file.type || 'application/octet-stream',
    })),
  };

  const url = buildAbsoluteUrl(
    config.apiBaseUrl,
    `/${accountId}/instruction-sheet-converter/presigned-upload`,
  );

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `署名付きURL取得に失敗しました: ${response.status}`;

    try {
      const errorData = JSON.parse(errorText);
      if (errorData && errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // JSON解析に失敗した場合はそのままテキストを使用
      if (errorText) {
        errorMessage = errorText;
      }
    }

    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * S3への直接ファイルアップロード
 *
 * ## 機能概要
 * 署名付きURLを使用してS3に直接ファイルをアップロードします。
 * XMLHttpRequestを使用して進捗を監視できます。
 *
 * ## 引数
 * - file: アップロード対象ファイル
 * - uploadUrl: 署名付きアップロードURL
 * - onProgress: 進捗コールバック関数（0-100の値）
 * - contentType: 送信するContent-Type。省略時は `file.type` を使う。
 *   Content-TypeはSigV4の署名対象に含まれるため、署名時と異なる値を送るとS3が403を返す。
 *   `File.type` がブラウザ依存で揺れる場合（Windowsの `.csv` が
 *   `application/vnd.ms-excel` になる等）は、署名に使った値を明示する
 *
 * ## 戻り値
 * アップロード完了のPromise
 */
export async function uploadFileToS3(
  file: File,
  uploadUrl: string,
  onProgress?: (progress: number) => void,
  contentType?: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // アップロード進捗の監視
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = (event.loaded / event.total) * 100;
        onProgress(progress);
      }
    });

    // アップロード完了の処理
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`S3アップロードに失敗しました: ${xhr.status} ${xhr.statusText}`));
      }
    });

    // エラー処理
    xhr.addEventListener('error', () => {
      reject(new Error('S3アップロード中にネットワークエラーが発生しました'));
    });

    // アップロード中止処理
    xhr.addEventListener('abort', () => {
      reject(new Error('S3アップロードが中止されました'));
    });

    // リクエスト送信
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType ?? file.type);
    xhr.send(file);
  });
}

/**
 * アップロード完了通知
 *
 * ## 機能概要
 * S3へのアップロード完了をバックエンドに通知し、変換処理を開始します。
 *
 * ## 引数
 * - jobId: ジョブID
 * - uploadedFiles: アップロード完了ファイル情報のリスト
 * - accountId: アカウントID
 * - authHeaders: 認証ヘッダー
 *
 * ## 戻り値
 * 変換処理開始レスポンス
 */
export async function notifyUploadComplete(
  jobId: string,
  uploadedFiles: UploadedFileInfo[],
  accountId: string,
  authHeaders?: Record<string, string>,
): Promise<InstructionSheetConverterResponse> {
  const config = getAppConfig();

  const request: UploadCompletionRequest = {
    job_id: jobId,
    uploaded_files: uploadedFiles,
  };

  const url = buildAbsoluteUrl(
    config.apiBaseUrl,
    `/${accountId}/instruction-sheet-converter/upload-complete`,
  );

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `アップロード完了通知に失敗しました: ${response.status}`;

    try {
      const errorData = JSON.parse(errorText);
      if (errorData && errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // JSON解析に失敗した場合はそのままテキストを使用
      if (errorText) {
        errorMessage = errorText;
      }
    }

    throw new Error(errorMessage);
  }

  return response.json();
}
